/**
 * Privacy-style web analytics tracker (framework-agnostic singleton).
 *
 * Posts batched events to `${REACT_APP_BACKEND_URL}/api/track` via
 * navigator.sendBeacon (with a fetch keepalive fallback). It tracks:
 *  - pageviews + engaged time (wall time only while the tab is visible)
 *  - max scroll depth per pageview
 *  - heatmap points (clicks always, moves throttled)
 *  - pitch-deck per-slide dwell time
 *
 * Tracking is fully DISABLED inside an iframe (the admin heatmap embeds the
 * live site) and on any /admin path, so the dashboard never pollutes data.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const TRACK_URL = `${BACKEND_URL}/api/track`;

const FLUSH_INTERVAL_MS = 5000;
const HEARTBEAT_INTERVAL_MS = 10000;
const ENGAGE_TICK_MS = 1000;
const MOVE_THROTTLE_MS = 200;
const MAX_HEATMAP_POINTS_PER_PV = 120;
const MIN_SLIDE_MS = 250;

const hasWindow = typeof window !== "undefined";

function uuid() {
  try {
    if (hasWindow && window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
  } catch (_) {
    /* fall through to manual */
  }
  // RFC4122-ish fallback for environments without crypto.randomUUID.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function clamp(n, lo, hi) {
  if (typeof n !== "number" || Number.isNaN(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
}

function inIframe() {
  try {
    return window.self !== window.top;
  } catch (_) {
    // Cross-origin access throws — that means we're framed.
    return true;
  }
}

function isAdminPath() {
  try {
    return (window.location.pathname || "").startsWith("/admin");
  } catch (_) {
    return false;
  }
}

/** Tracking is enabled only on the real site, never framed, never on /admin. */
export function isEnabled() {
  if (!hasWindow) return false;
  if (inIframe()) return false;
  if (isAdminPath()) return false;
  return true;
}

function getVid() {
  try {
    let vid = window.localStorage.getItem("ggl_vid");
    if (!vid) {
      vid = uuid();
      window.localStorage.setItem("ggl_vid", vid);
    }
    return vid;
  } catch (_) {
    // Storage blocked — fall back to a per-load id so events still group.
    if (!getVid._mem) getVid._mem = uuid();
    return getVid._mem;
  }
}

function getSid() {
  try {
    let sid = window.sessionStorage.getItem("ggl_sid");
    if (!sid) {
      sid = uuid();
      window.sessionStorage.setItem("ggl_sid", sid);
    }
    return sid;
  } catch (_) {
    if (!getSid._mem) getSid._mem = uuid();
    return getSid._mem;
  }
}

function currentCtx() {
  let ua = "";
  let language = "";
  let referrer = "";
  let path = "";
  let screen = "";
  try {
    ua = window.navigator.userAgent || "";
  } catch (_) {}
  try {
    language = window.navigator.language || "";
  } catch (_) {}
  try {
    referrer = document.referrer || "";
  } catch (_) {}
  try {
    path = window.location.pathname || "";
  } catch (_) {}
  try {
    if (window.screen) screen = `${window.screen.width}x${window.screen.height}`;
  } catch (_) {}
  return {
    ua,
    language,
    screen,
    viewport_w: window.innerWidth || 0,
    viewport_h: window.innerHeight || 0,
    referrer,
    path,
  };
}

// ---------------------------------------------------------------------------
// Event queue + flushing
// ---------------------------------------------------------------------------

let queue = [];

function enqueue(event) {
  if (!isEnabled()) return;
  queue.push(event);
}

function sendPayload(payload) {
  const body = JSON.stringify(payload);
  // NOTE: use a text/plain content type (not application/json). It keeps the
  // cross-origin request a CORS "simple request" — no preflight, no credentials
  // gate — so the beacon always reaches the server. application/json forces a
  // preflight that sendBeacon can't satisfy, and the POST gets silently dropped.
  // The backend parses the raw body, so the content type doesn't matter to it.
  const TYPE = "text/plain;charset=UTF-8";
  // Preferred: sendBeacon (survives unload, non-blocking).
  try {
    if (navigator && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: TYPE });
      const ok = navigator.sendBeacon(TRACK_URL, blob);
      if (ok) return;
    }
  } catch (_) {
    /* fall through to fetch */
  }
  // Fallback: fetch with keepalive so it also survives unload.
  try {
    fetch(TRACK_URL, {
      method: "POST",
      headers: { "Content-Type": TYPE },
      body,
      keepalive: true,
      // Public ingest — keep it credential-free to match server CORS.
      credentials: "omit",
    }).catch(() => {});
  } catch (_) {
    /* give up silently — analytics must never break the page */
  }
}

function flush() {
  if (!isEnabled()) {
    queue = [];
    return;
  }
  if (queue.length === 0) return;
  const events = queue;
  queue = [];
  sendPayload({
    vid: getVid(),
    sid: getSid(),
    ctx: currentCtx(),
    events,
  });
}

// ---------------------------------------------------------------------------
// Pageview + engagement tracking
// ---------------------------------------------------------------------------

let pv = null; // { pv_id, path, startedAt, engagedMs, lastEngageStart, maxScrollPct, heatmapCount }

function now() {
  return Date.now();
}

/** Accumulate engaged time since the last tick while the tab is visible. */
function accrueEngaged() {
  if (!pv || pv.lastEngageStart == null) return;
  const delta = now() - pv.lastEngageStart;
  if (delta > 0) pv.engagedMs += delta;
  pv.lastEngageStart = now();
}

function startEngageClock() {
  if (!pv) return;
  if (pv.lastEngageStart == null) pv.lastEngageStart = now();
}

function stopEngageClock() {
  if (!pv) return;
  accrueEngaged();
  pv.lastEngageStart = null;
}

function computeScrollPct() {
  try {
    const doc = document.documentElement;
    const docHeight = Math.max(
      doc.scrollHeight,
      document.body ? document.body.scrollHeight : 0,
      window.innerHeight || 0
    );
    if (docHeight <= 0) return 0;
    const scrolled = (window.scrollY || window.pageYOffset || 0) + (window.innerHeight || 0);
    return clamp(Math.round((scrolled / docHeight) * 100), 0, 100);
  } catch (_) {
    return 0;
  }
}

export function startPageview(path) {
  if (!isEnabled()) return;
  const p = path || (window.location.pathname || "/");
  pv = {
    pv_id: uuid(),
    path: p,
    startedAt: now(),
    engagedMs: 0,
    lastEngageStart: null,
    maxScrollPct: 0,
    heatmapCount: 0,
    lastMove: 0,
  };
  // Start the engage clock if the tab is currently visible.
  if (typeof document === "undefined" || document.visibilityState === "visible") {
    startEngageClock();
  }
  enqueue({
    type: "pageview",
    pv_id: pv.pv_id,
    path: p,
    referrer: (() => {
      try {
        return document.referrer || "";
      } catch (_) {
        return "";
      }
    })(),
    viewport_w: window.innerWidth || 0,
    viewport_h: window.innerHeight || 0,
  });
}

export function flushPageTime() {
  if (!isEnabled() || !pv) return;
  accrueEngaged();
  enqueue({
    type: "page_time",
    pv_id: pv.pv_id,
    path: pv.path,
    duration_ms: Math.round(pv.engagedMs),
    max_scroll_pct: clamp(Math.round(pv.maxScrollPct), 0, 100),
  });
}

export function endPageview() {
  if (!pv) return;
  flushPageTime();
  stopEngageClock();
  pv = null;
}

// ---------------------------------------------------------------------------
// Heatmap capture
// ---------------------------------------------------------------------------

function recordHeatmap(kind, pageX, pageY) {
  if (!isEnabled() || !pv) return;
  if (pv.heatmapCount >= MAX_HEATMAP_POINTS_PER_PV) return;
  let xpct;
  let ypct;
  try {
    const docW = document.documentElement.scrollWidth || window.innerWidth || 1;
    const docH = document.documentElement.scrollHeight || window.innerHeight || 1;
    xpct = clamp(pageX / docW, 0, 1);
    ypct = clamp(pageY / docH, 0, 1);
  } catch (_) {
    return;
  }
  pv.heatmapCount += 1;
  enqueue({
    type: "heatmap",
    path: pv.path,
    kind,
    xpct,
    ypct,
  });
}

function onClick(e) {
  recordHeatmap("click", e.pageX, e.pageY);
}

function onMouseMove(e) {
  if (!pv) return;
  const t = now();
  if (t - (pv.lastMove || 0) < MOVE_THROTTLE_MS) return;
  pv.lastMove = t;
  recordHeatmap("move", e.pageX, e.pageY);
}

function onScroll() {
  if (!pv) return;
  const pct = computeScrollPct();
  if (pct > pv.maxScrollPct) pv.maxScrollPct = pct;
}

// ---------------------------------------------------------------------------
// Slide tracking (pitch deck)
// ---------------------------------------------------------------------------

export function trackSlide(slideIndex, slideN, durationMs) {
  if (!isEnabled()) return;
  const d = Math.round(durationMs);
  if (!(d >= MIN_SLIDE_MS)) return;
  enqueue({
    type: "slide",
    slide_index: typeof slideIndex === "number" ? slideIndex : 0,
    slide_n: slideN || "",
    duration_ms: d,
  });
}

// ---------------------------------------------------------------------------
// Lifecycle wiring (idempotent)
// ---------------------------------------------------------------------------

let started = false;
let flushTimer = null;
let heartbeatTimer = null;
let engageTimer = null;

function onVisibilityChange() {
  if (typeof document === "undefined") return;
  if (document.visibilityState === "visible") {
    startEngageClock();
  } else {
    // Tab hidden — bank engaged time and flush what we have.
    stopEngageClock();
    flushPageTime();
    flush();
  }
}

function onPageHide() {
  flushPageTime();
  flush();
}

function heartbeat() {
  if (!pv) return;
  // Keep engaged time fresh and push an idempotent page_time upsert.
  if (typeof document === "undefined" || document.visibilityState === "visible") {
    accrueEngaged();
  }
  flushPageTime();
  flush();
}

/**
 * Attach global listeners + timers exactly once. Safe to call multiple times.
 */
export function init() {
  if (started || !hasWindow) return;
  started = true;

  // Heatmap + scroll capture (passive where possible; harmless when disabled
  // because the handlers early-return via isEnabled()/pv guards).
  try {
    window.addEventListener("click", onClick, { capture: true });
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
  } catch (_) {}

  // Periodic engage-clock tick so long visible dwell stays counted between
  // heartbeats even with no other events.
  engageTimer = window.setInterval(() => {
    if (pv && (typeof document === "undefined" || document.visibilityState === "visible")) {
      accrueEngaged();
    }
  }, ENGAGE_TICK_MS);

  flushTimer = window.setInterval(flush, FLUSH_INTERVAL_MS);
  heartbeatTimer = window.setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
}

const analytics = {
  isEnabled,
  init,
  startPageview,
  flushPageTime,
  endPageview,
  trackSlide,
  flush,
};

export default analytics;
